<template>
  <span :class="classes" :title="team.name">
    <img
      v-if="imageSrc"
      :src="imageSrc"
      :alt="team.tag"
      :class="$style.image"
      loading="lazy"
    />
    <span v-else :class="$style.fallback">
      {{ tagShort }}
    </span>
  </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { PickemTeam } from '../../types'

defineOptions({ name: 'PickemTeamIcon' })

const props = withDefaults(
  defineProps<{
    team: PickemTeam
    // 'xs' 24px · 's' 32px · 'sm' 30px · 'm' 40px · 'slot' 50px · 'l' 56px
    size?: 'xs' | 's' | 'sm' | 'm' | 'slot' | 'l'
    // Декоративный border-overlay вокруг иконки (для is_correct).
    outline?: 'success' | 'danger' | null
    // Без фонового круга — логотип на фоне родителя (палитра Swiss).
    bare?: boolean
  }>(),
  {
    size: 'm',
    outline: null,
    bare: false
  }
)

const cssModule = useCssModule()

const classes = computed(() => [
  cssModule.icon,
  cssModule[`size-${props.size}`],
  props.bare && cssModule.bare,
  props.outline === 'success' && cssModule['outline-success'],
  props.outline === 'danger' && cssModule['outline-danger']
])

const imageSrc = computed(() => {
  if (!props.team.image) return null
  if (props.size === 'l') return props.team.image.large
  if (props.size === 'xs' || props.size === 's') return props.team.image.small
  return props.team.image.medium ?? props.team.image.small
})

// Если у команды нет картинки — рисуем первые 4 символа тега в кружке.
const tagShort = computed(() => props.team.tag.slice(0, 4))
</script>

<style lang="scss" module>
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: color-get(--root-rgb-bg-default);
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.15s ease;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.fallback {
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-2);
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.size-xs {
  width: 2.4em;
  height: 2.4em;
}

.size-s {
  width: 3.2em;
  height: 3.2em;
}

.size-sm {
  width: 3em;
  height: 3em;
}

.size-m {
  width: 4em;
  height: 4em;
}

.size-slot {
  width: 5em;
  height: 5em;
}

.size-l {
  width: 5.6em;
  height: 5.6em;
}

.bare {
  background-color: transparent;
  border-color: transparent;
}

.outline-success {
  border-color: color-get(--root-rgb-accent-2);
}

.outline-danger {
  border-color: color-get(--root-rgb-system-error);
}
</style>
